import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { readPerWindow, readPerWindowScoped, writePerWindow, writePerWindowScoped } from 'features/load-hub/utils/perWindowStorage'
import { normalizeNumeoIdQuery } from '@numeo/types'
import { type BoxTruckRefinements } from '../components/BoxTruckRefinementBar'
import { sanitizeHiddenSources, sanitizeRefinements } from '../utils/refinementSerialization'
import { BOX_TRUCK_WORKSPACE_TTL_MS } from '../utils/boxTruckTabExpiration'
import { isRecord } from '@/utils/isRecord'

const STORAGE_KEY = 'box_truck_view_prefs'

/**
 * How long the Driver Bids bucket survives.
 *
 * Every OTHER bucket is keyed by a search tab id and inherits that tab's own expiry for free:
 * useBoxTruckTabs purges a tab seven days after it was CREATED, and this record drops any bucket whose key is no
 * longer a live tab. Driver Bids is the one board with no tab to expire alongside, so it carries its own
 * stamp. Same reasoning as useBoxTruckDatEnabled: these are NARROWING filters, and a Min $ an operator
 * forgot setting last week shows them four loads today and reads as a broken feed.
 */
const DRIVER_BIDS_TTL_MS = 24 * 60 * 60 * 1000

/** Refinements are typed character by character, and every write does a registry read, a write and a GC
 *  sweep. Coalesce a burst of keystrokes into one write. */
const WRITE_DEBOUNCE_MS = 300

/** Same bound the browser-tab handoff puts on the find term. */
const MAX_QUERY_LENGTH = 200

type BoxTruckRefinementsByTab = Record<string, BoxTruckRefinements>
type BoxTruckHiddenSourcesByTab = Record<string, Set<string>>
type BoxTruckQueryByTab = Record<string, string>

interface StoredViewPrefs {
    refinementsByTab: Record<string, BoxTruckRefinements>
    hiddenSourcesByTab: Record<string, string[]>
    queryByTab: Record<string, string>
    /** Dated by the last CHANGE to ANY Driver Bids bucket; see DRIVER_BIDS_TTL_MS. */
    driverBidsAt?: number
}

interface UseBoxTruckViewPrefsOptions {
    /** Every live board key — each search tab id plus the reserved Driver Bids key. A bucket outside this
     *  set is dropped on read AND on write. */
    validKeys: string[]
    /** The reserved Driver Bids key: always valid, so no tab purge ever reaches it. */
    driverBidsKey: string
    /** A browser-tab handoff supersedes storage entirely — a detached tab must not inherit the source
     *  window's other boards. */
    handoffRequested: boolean
    importedRefinements?: BoxTruckRefinements
    importedHiddenSources?: string[]
    importedQuery?: string
    /** Board key the imported values belong to (the detached tab's single tab id). */
    importedKey?: string
    /** Detached windows read and write only their own namespaced keys. */
    isolatedWindow: boolean
    /** False while a detached window is still validating identity — no writes until it settles. */
    persist: boolean
}

interface BoxTruckViewPrefsApi {
    refinementsByTab: BoxTruckRefinementsByTab
    setRefinementsByTab: Dispatch<SetStateAction<BoxTruckRefinementsByTab>>
    hiddenSourcesByTab: BoxTruckHiddenSourcesByTab
    setHiddenSourcesByTab: Dispatch<SetStateAction<BoxTruckHiddenSourcesByTab>>
    queryByTab: BoxTruckQueryByTab
    setQueryByTab: Dispatch<SetStateAction<BoxTruckQueryByTab>>
}

/** The find term as TYPED, bounded. Restored as a client-side row filter only — exactly what an untouched
 *  find box already does before Search is pressed.
 *
 *  A term that reads as a Numeo ID is deliberately NOT persisted. Pressing Search on one is a SERVER lookup
 *  that returns a single row across all pages; a reload re-runs the lane with `numeoId: null`, so restoring
 *  the term would apply it as a substring filter over lane rows it will almost never match — a lane that has
 *  loads would render empty. Dropping it restores the lane instead of a board that looks broken. */
const persistableQuery = (value: unknown): string => {
    if (typeof value !== 'string') return ''
    const term = value.slice(0, MAX_QUERY_LENGTH)
    return normalizeNumeoIdQuery(term) ? '' : term
}

/** Whether the Driver Bids board holds any client-side filter at all. The stamp and the change snapshot
 *  both read this, so the three buckets can never be gated on a date that only one of them can refresh. */
const hasDriverBidsData = (prefs: { refinementsByTab: object; hiddenSourcesByTab: object; queryByTab: object }, driverBidsKey: string): boolean =>
    driverBidsKey in prefs.refinementsByTab || driverBidsKey in prefs.hiddenSourcesByTab || driverBidsKey in prefs.queryByTab

/** Drop every bucket whose board no longer exists. Two routes leave one behind and only this covers both:
 *  closing a tab deletes the bucket in page state (so the next write persists the deletion), but a tab
 *  purged by its seven-day TTL inside useBoxTruckTabs' restore never touches page state at all. */
const prune = <T>(buckets: Record<string, T>, validKeys: Set<string>): Record<string, T> => {
    const kept: Record<string, T> = {}
    for (const [key, bucket] of Object.entries(buckets)) if (validKeys.has(key)) kept[key] = bucket
    return kept
}

interface RestoredViewPrefs {
    refinementsByTab: BoxTruckRefinementsByTab
    hiddenSourcesByTab: BoxTruckHiddenSourcesByTab
    queryByTab: BoxTruckQueryByTab
    /** The stamp as STORED, carried through the restore so a reload does not restart the Driver Bids clock. */
    driverBidsAt?: number
}

const EMPTY_RESTORED: RestoredViewPrefs = { refinementsByTab: {}, hiddenSourcesByTab: {}, queryByTab: {} }

const readPersisted = (
    { validKeys, driverBidsKey, isolatedWindow }: Pick<UseBoxTruckViewPrefsOptions, 'validKeys' | 'driverBidsKey' | 'isolatedWindow'>,
    now: number
): RestoredViewPrefs => {
    try {
        const raw = isolatedWindow ? readPerWindowScoped(STORAGE_KEY) : readPerWindow(STORAGE_KEY)
        if (!raw) return EMPTY_RESTORED
        const stored = JSON.parse(raw) as StoredViewPrefs | null
        if (!isRecord(stored)) return EMPTY_RESTORED

        const live = new Set(validKeys)
        // Driver Bids has no tab to expire with, so its own stamp decides. Anything undatable reads as
        // expired — falling back to an unfiltered board is the safe direction.
        const driverBidsAt = typeof stored.driverBidsAt === 'number' && Number.isFinite(stored.driverBidsAt) ? stored.driverBidsAt : 0
        // A future stamp means a clock that moved backwards, not a choice made later; treat elapsed as zero.
        const driverBidsLive = driverBidsAt > now || now - driverBidsAt < DRIVER_BIDS_TTL_MS
        if (!driverBidsLive) live.delete(driverBidsKey)

        const refinementsByTab: BoxTruckRefinementsByTab = {}
        for (const [key, bucket] of Object.entries(isRecord(stored.refinementsByTab) ? stored.refinementsByTab : {})) {
            if (live.has(key)) refinementsByTab[key] = sanitizeRefinements(bucket)
        }

        const hiddenSourcesByTab: BoxTruckHiddenSourcesByTab = {}
        for (const [key, bucket] of Object.entries(isRecord(stored.hiddenSourcesByTab) ? stored.hiddenSourcesByTab : {})) {
            const sources = sanitizeHiddenSources(bucket)
            if (live.has(key) && sources.length > 0) hiddenSourcesByTab[key] = new Set(sources)
        }

        const queryByTab: BoxTruckQueryByTab = {}
        for (const [key, term] of Object.entries(isRecord(stored.queryByTab) ? stored.queryByTab : {})) {
            const sanitized = persistableQuery(term)
            if (live.has(key) && sanitized) queryByTab[key] = sanitized
        }

        return { refinementsByTab, hiddenSourcesByTab, queryByTab, ...(driverBidsLive && driverBidsAt ? { driverBidsAt } : {}) }
    } catch {
        // Blocked storage, private mode, or a payload someone hand-edited — persistence is best-effort and
        // must never cost the operator a usable board.
        return EMPTY_RESTORED
    }
}

const writePersisted = (
    prefs: RestoredViewPrefs,
    { validKeys, driverBidsKey, isolatedWindow }: Pick<UseBoxTruckViewPrefsOptions, 'validKeys' | 'driverBidsKey' | 'isolatedWindow'>,
    driverBidsAt: number | undefined
): void => {
    try {
        const live = new Set(validKeys)
        const refinementsByTab = prune(prefs.refinementsByTab, live)
        const hiddenSourcesByTab: Record<string, string[]> = {}
        for (const [key, sources] of Object.entries(prune(prefs.hiddenSourcesByTab, live))) {
            const list = sanitizeHiddenSources([...sources])
            // Store only boards that actually hide something, so an untouched Sources bar leaves no record.
            if (list.length > 0) hiddenSourcesByTab[key] = list
        }
        const queryByTab: Record<string, string> = {}
        for (const [key, term] of Object.entries(prune(prefs.queryByTab, live))) {
            const sanitized = persistableQuery(term)
            // Only boards with a term, so an untouched find box leaves no record.
            if (sanitized) queryByTab[key] = sanitized
        }
        const payload: StoredViewPrefs = {
            refinementsByTab,
            hiddenSourcesByTab,
            queryByTab,
            // Stamped while ANY Driver Bids bucket holds something, not just the refinements. The read side
            // gates all three on this one date, so stamping on refinements alone meant a find term or a
            // hidden chip set on Driver Bids WITHOUT a refinement was written unstamped and discarded as
            // expired on the very next reload — the bug this hook exists to fix, reintroduced for that board.
            ...(hasDriverBidsData({ refinementsByTab, hiddenSourcesByTab, queryByTab }, driverBidsKey) && driverBidsAt ? { driverBidsAt } : {}),
        }
        const write = isolatedWindow ? writePerWindowScoped : writePerWindow
        write(STORAGE_KEY, JSON.stringify(payload), BOX_TRUCK_WORKSPACE_TTL_MS)
    } catch {
        // Storage disabled / full — keep the in-memory board, drop persistence.
    }
}

/**
 * Persist + restore the Box Truck loadboard's CLIENT-SIDE filters across a reload: the refinement values
 * (trip miles, rate, weight, load age, required contact) and the Sources-bar chips.
 *
 * These are the filters applied over rows the board already fetched, as opposed to the lane/date/equipment
 * set that becomes part of the search REQUEST (useBoxTruckFilterPersistence + useBoxTruckTabs). Only the
 * request half was ever persisted, so an operator who narrowed a board and pressed F5 silently got a
 * different board back.
 *
 * Both are kept PER BOARD — one bucket per search tab, plus the reserved Driver Bids key — so a value set
 * on one lane never follows the operator to another. Per-window scoping matches every other box-truck
 * store: two monitors stay independent, while a reload reclaims the same window id and its values.
 */
export const useBoxTruckViewPrefs = ({
    validKeys,
    driverBidsKey,
    handoffRequested,
    importedRefinements,
    importedHiddenSources,
    importedQuery,
    importedKey,
    isolatedWindow,
    persist,
}: UseBoxTruckViewPrefsOptions): BoxTruckViewPrefsApi => {
    const [restored] = useState<RestoredViewPrefs>(() => {
        // A detached browser tab represents ONE board. Seeding it from storage would clone every other
        // board the source window had open.
        const base = handoffRequested ? EMPTY_RESTORED : readPersisted({ validKeys, driverBidsKey, isolatedWindow }, Date.now())
        if (!importedKey) return base
        const importedSources = sanitizeHiddenSources(importedHiddenSources)
        const importedTerm = persistableQuery(importedQuery)
        return {
            refinementsByTab: importedRefinements
                ? { ...base.refinementsByTab, [importedKey]: sanitizeRefinements(importedRefinements) }
                : base.refinementsByTab,
            hiddenSourcesByTab: importedSources.length ? { ...base.hiddenSourcesByTab, [importedKey]: new Set(importedSources) } : base.hiddenSourcesByTab,
            queryByTab: importedTerm ? { ...base.queryByTab, [importedKey]: importedTerm } : base.queryByTab,
        }
    })

    const [refinementsByTab, setRefinementsByTab] = useState<BoxTruckRefinementsByTab>(restored.refinementsByTab)
    const [hiddenSourcesByTab, setHiddenSourcesByTab] = useState<BoxTruckHiddenSourcesByTab>(restored.hiddenSourcesByTab)
    const [queryByTab, setQueryByTab] = useState<BoxTruckQueryByTab>(restored.queryByTab)

    // The Driver Bids clock must measure time since the bucket last CHANGED, not since the page last wrote.
    // Every mount writes the record back (restoring it is itself a state change), so minting a fresh stamp on
    // each write would let an operator who merely OPENS the board each day keep a Min $ they set weeks ago —
    // exactly the forgotten-filter case the expiry exists to end. Compare against the last value written and
    // re-stamp only on a real edit.
    const driverBidsSnapshot = useCallback(
        (prefs: RestoredViewPrefs): string =>
            JSON.stringify([
                prefs.refinementsByTab[driverBidsKey] ?? null,
                [...(prefs.hiddenSourcesByTab[driverBidsKey] ?? [])].sort(),
                prefs.queryByTab[driverBidsKey] ?? null,
            ]),
        [driverBidsKey]
    )
    const driverBidsAtRef = useRef(restored.driverBidsAt)
    const driverBidsSnapshotRef = useRef(driverBidsSnapshot(restored))
    const resolveDriverBidsAt = useCallback(
        (prefs: RestoredViewPrefs): number | undefined => {
            const snapshot = driverBidsSnapshot(prefs)
            if (snapshot !== driverBidsSnapshotRef.current) {
                driverBidsSnapshotRef.current = snapshot
                driverBidsAtRef.current = Date.now()
            }
            return driverBidsAtRef.current
        },
        [driverBidsSnapshot]
    )

    // The debounced write reads the LATEST values from here rather than the ones captured when the timer
    // was scheduled — otherwise switching boards mid-debounce persists a stale record.
    const latestRef = useRef({ refinementsByTab, hiddenSourcesByTab, queryByTab, validKeys, driverBidsKey, isolatedWindow, persist })
    latestRef.current = { refinementsByTab, hiddenSourcesByTab, queryByTab, validKeys, driverBidsKey, isolatedWindow, persist }

    useEffect(() => {
        if (!persist) return
        const timer = setTimeout(() => {
            const { refinementsByTab: r, hiddenSourcesByTab: h, queryByTab: q, ...scope } = latestRef.current
            writePersisted(
                { refinementsByTab: r, hiddenSourcesByTab: h, queryByTab: q },
                scope,
                resolveDriverBidsAt({ refinementsByTab: r, hiddenSourcesByTab: h, queryByTab: q })
            )
        }, WRITE_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [refinementsByTab, hiddenSourcesByTab, queryByTab, validKeys, driverBidsKey, isolatedWindow, persist, resolveDriverBidsAt])

    /**
     * Flush the pending write.
     *
     * React unmount alone is NOT enough: a hard reload or a tab close tears the page down without unmounting
     * anything, so a value typed inside the debounce window would be lost on exactly the action this hook
     * exists to survive. `pagehide` covers reload, close and navigation (including the bfcache path, where
     * `beforeunload` is unreliable); `visibilitychange` catches the mobile/background case where `pagehide`
     * may never fire.
     */
    const flush = useCallback(() => {
        const { refinementsByTab: r, hiddenSourcesByTab: h, queryByTab: q, persist: shouldPersist, ...scope } = latestRef.current
        if (!shouldPersist) return
        const prefs = { refinementsByTab: r, hiddenSourcesByTab: h, queryByTab: q }
        writePersisted(prefs, scope, resolveDriverBidsAt(prefs))
    }, [resolveDriverBidsAt])

    useEffect(() => {
        const onHide = () => {
            if (document.visibilityState === 'hidden') flush()
        }
        window.addEventListener('pagehide', flush)
        document.addEventListener('visibilitychange', onHide)
        return () => {
            window.removeEventListener('pagehide', flush)
            document.removeEventListener('visibilitychange', onHide)
            flush()
        }
    }, [flush])

    return { refinementsByTab, setRefinementsByTab, hiddenSourcesByTab, setHiddenSourcesByTab, queryByTab, setQueryByTab }
}
